// web/src/data/offersV2.ts
import {
  addDoc,
  collection,
  serverTimestamp,
  Timestamp,
  updateDoc,
  doc,
  setDoc,
  onSnapshot,
  query,
  orderBy,
  getDoc,
} from "firebase/firestore";
import { db, auth } from "@/lib/firebase";

const MINIMAL_OFFER_SNAPSHOT =
  (import.meta as any)?.env?.VITE_MINIMAL_OFFER_SNAPSHOT === "true";

export interface SendOfferInput {
  toUid: string;
  offerType: "trade" | "purchase";
  // Optional numeric price (only meaningful for purchase)
  offeredPrice?: number | null;
  message?: string | null;
  preview?: string | null;

  // Card snapshot / identifiers
  cardId?: string | null;
  cardOwnerUid?: string | null;
  cardRefPath?: string | null;
  cardName?: string | null;
  cardImage?: string | null;
  cardSet?: string | null;
  cardNumber?: string | null;
  cardFoil?: boolean;
  cardCondition?: string | null;
  cardQty?: number | null;
  cardStatus?: string | null;
  listingPrice?: number | null;
  currency?: string | null;

  // Legacy / additional
  scryfall_id?: string | null;
  oracle_id?: string | null;
  offeredItems?: Array<{ name?: string; scryfall_id?: string; cardId?: string }>;
}

export interface OfferDocStored extends SendOfferInput {
  id?: string;
  fromUid: string;
  toUid: string;
  status: "open" | "accepted" | "declined" | "withdrawn" | "completed" | "countered";
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
  lastActorUid: string | null;
}

function cleanTrim(s?: string | null): string | null {
  if (!s) return null;
  const t = s.trim();
  return t.length ? t : null;
}

function coercePrice(n: any): number | null {
  if (n === null || n === undefined || n === "") return null;
  const v = Number(n);
  if (!Number.isFinite(v)) return null;
  return v;
}

function derivePreview(msg: string | null, existing: string | null): string | null {
  if (existing) return existing.slice(0, 160);
  if (!msg) return null;
  return msg.slice(0, 160);
}

function deriveCardRefPath(input: SendOfferInput): string | null {
  if (input.cardRefPath) return input.cardRefPath;
  if (input.cardOwnerUid && input.cardId) {
    return `users/${input.cardOwnerUid}/cards/${input.cardId}`;
  }
  return null;
}

function resolveScryfallId(input: SendOfferInput): string | null {
  if (input.scryfall_id) return input.scryfall_id;
  if (input.offeredItems?.length) {
    const s = input.offeredItems[0]?.scryfall_id;
    if (s) return s;
  }
  return null;
}

// Small helper: pick shortest available image reference (thumb > small > normal)
function pickCardImage(raw: SendOfferInput): string | null {
  const anyRaw = raw as any;
  return (
    anyRaw.cardThumb ||
    anyRaw.cardImageThumb ||
    anyRaw.thumb ||
    anyRaw.image_thumb ||
    anyRaw.cardImageSmall ||
    anyRaw.image_small ||
    raw.cardImage ||
    null
  );
}

// Build snapshot respecting minimal mode
function buildCardSnapshot(raw: SendOfferInput, cardRefPath: string | null, scryfall_id: string | null, offeredPrice: number | null) {
  const base = {
    cardId: raw.cardId ?? null,
    cardOwnerUid: raw.cardOwnerUid ?? null,
    cardRefPath,
    cardName: raw.cardName ?? null,
    cardImage: pickCardImage(raw),
    offeredPrice,          // keep main price always (already top-level but kept here for clarity)
    scryfall_id,
  };

  if (MINIMAL_OFFER_SNAPSHOT) {
    return base; // only essentials
  }

  // Extended (legacy) fields retained if not in minimal mode
  return {
    ...base,
    cardSet: raw.cardSet ?? null,
    cardNumber: raw.cardNumber ?? null,
    cardFoil: !!raw.cardFoil,
    cardCondition: raw.cardCondition ?? null,
    cardQty: raw.cardQty ?? 1,
    cardStatus: raw.cardStatus ?? null,
    listingPrice: raw.listingPrice ?? null,
    currency: raw.currency ?? "USD",
    oracle_id: raw.oracle_id ?? null,
  };
}

/**
 * sendOfferV2
 * Writes an offer document with full snapshot metadata for immediate UI render + email generation.
 * Returns created doc id.
 */
export async function sendOfferV2(raw: SendOfferInput): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new Error("Not signed in");
  if (!raw.toUid) throw new Error("Missing toUid");
  if (raw.toUid === user.uid) throw new Error("Cannot offer to yourself");
  if (raw.offerType !== "trade" && raw.offerType !== "purchase") {
    throw new Error("Invalid offerType");
  }

  const message = cleanTrim(raw.message);
  const offeredPrice =
    raw.offerType === "purchase" ? coercePrice(raw.offeredPrice) : null;
  const cardRefPath = deriveCardRefPath(raw);
  const preview = derivePreview(message, cleanTrim(raw.preview));
  const scryfall_id = resolveScryfallId(raw);

  const cardSnapshot = buildCardSnapshot(raw, cardRefPath, scryfall_id, offeredPrice);

  const docData: any = {
    fromUid: user.uid,
    toUid: raw.toUid,
    offerType: raw.offerType,
    offeredPrice,       // top-level for querying
    message,
    preview,
    offeredItems: raw.offeredItems ?? null,
    status: "open",
    createdAt: null,
    updatedAt: null,
    lastActorUid: user.uid,
    // snapshot fields (minimal or extended)
    ...cardSnapshot,
  };

  // If minimal mode, strip unused extended fields accidentally provided
  if (MINIMAL_OFFER_SNAPSHOT) {
    delete docData.cardSet;
    delete docData.cardNumber;
    delete docData.cardFoil;
    delete docData.cardCondition;
    delete docData.cardQty;
    delete docData.cardStatus;
    delete docData.listingPrice;
    delete docData.currency;
    delete docData.oracle_id;
  }

  // Firestore write
  const ref = await addDoc(collection(db, "offers_v2"), {
    ...docData,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  // Lightweight thread touch
  try {
    const tidParts = [user.uid, raw.toUid].sort((a, b) => (a < b ? -1 : 1));
    const threadId = `dm2:${tidParts[0]}_${tidParts[1]}`;
    await setDoc(
      doc(db, "threads_v2", threadId),
      {
        participants: tidParts,
        lastMessage: message
          ? message.slice(0, 120)
          : offeredPrice != null
          ? `Offer: ${offeredPrice}`
          : "New offer",
        lastMessageAt: serverTimestamp(),
        lastSenderId: user.uid,
        [`unreadCounts.${raw.toUid}`]: 1,
        tab: "inbox",
      },
      { merge: true },
    );
  } catch {}

  return ref.id;
}

/**
 * Optional helper to update core monetary fields (counter).
 */
export async function counterOfferV2(offerId: string, newPrice: number, actorUid: string) {
  const amt = coercePrice(newPrice);
  if (amt == null) throw new Error("Invalid amount");
  await updateDoc(doc(db, "offers_v2", offerId), {
    offeredPrice: amt,
    status: "countered",
    lastActorUid: actorUid,
    updatedAt: serverTimestamp(),
  });
}

// ============ NEW TYPES / HELPERS ============
export interface OfferMessage {
  id: string;
  text: string;
  senderUid: string;
  createdAt: Timestamp | null;
  _synthetic?: boolean;
}

/**
 * Subscribe to offer message thread (subcollection: offers_v2/{offerId}/messages)
 */
export function subscribeOfferMessages(
  offerId: string,
  cb: (msgs: OfferMessage[]) => void,
) {
  const qref = query(
    collection(db, "offers_v2", offerId, "messages"),
    orderBy("createdAt", "asc"),
  );
  return onSnapshot(qref, (snap) => {
    const out: OfferMessage[] = [];
    snap.forEach((d) => {
      const v: any = d.data();
      out.push({
        id: d.id,
        text: v.text || "",
        senderUid: v.senderUid,
        createdAt: v.createdAt || null,
      });
    });
    cb(out);
  });
}

/**
 * Send a message in an offer discussion.
 */
export async function sendOfferMessage(offerId: string, text: string) {
  const u = auth.currentUser;
  if (!u) throw new Error("Not signed in");
  const body = text.trim();
  if (!body) return;
  await addDoc(collection(db, "offers_v2", offerId, "messages"), {
    text: body,
    senderUid: u.uid,
    createdAt: serverTimestamp(),
  });
  // touch offer updatedAt
  await updateDoc(doc(db, "offers_v2", offerId), {
    updatedAt: serverTimestamp(),
    lastActorUid: u.uid,
  });
}

/**
 * Ensure cardName / cardImage are populated on the offer by reading the cardRefPath doc.
 * Safe no-op if already present or path missing.
 */
export async function ensureOfferCardSnapshot(opts: {
  id: string;
  cardRefPath: string | null;
}) {
  if (!opts.cardRefPath) return;
  const segs = opts.cardRefPath.split("/");
  if (segs.length !== 4) return;
  try {
    const offerRef = doc(db, "offers_v2", opts.id);
    // Read card
    const cardSnap = await getDoc(doc(db, segs[0], segs[1], segs[2], segs[3]));
    if (!cardSnap.exists()) return;
    const v: any = cardSnap.data();
    const name =
      v.name ||
      v.printed_name ||
      v.faceName ||
      v.face_name ||
      v.safe_name ||
      null;
    const image =
      v.thumb ||
      v.cardThumb ||
      v.image_thumb ||
      v.image_small ||
      v.cardImageThumb ||
      v.image ||
      v.image_normal ||
      v.image_large ||
      v.custom_image_url ||
      v.img ||
      null;

    // Only update if missing
    if (!name && !image) return;

    await updateDoc(offerRef, {
      ...(name ? { cardName: name } : {}),
      ...(image ? { cardImage: image } : {}),
      updatedAt: serverTimestamp(),
    });
  } catch {}
}

// ============ STATUS ACTIONS ============
export async function acceptOfferV2(offerId: string, actorUid: string) {
  await updateDoc(doc(db, "offers_v2", offerId), {
    status: "accepted",
    lastActorUid: actorUid,
    updatedAt: serverTimestamp(),
  });
}

export async function declineOfferV2(offerId: string, actorUid: string) {
  await updateDoc(doc(db, "offers_v2", offerId), {
    status: "declined",
    lastActorUid: actorUid,
    updatedAt: serverTimestamp(),
  });
}

export async function rescindOfferV2(offerId: string, actorUid: string) {
  await updateDoc(doc(db, "offers_v2", offerId), {
    status: "withdrawn",
    lastActorUid: actorUid,
    updatedAt: serverTimestamp(),
  });
}

// export existing function under new name
export function safeSendOfferV2(input: SendOfferV2Input) {
  return sendOfferV2(input);
}


